"use client";

import { useEffect } from "react";
import { applyTheme, getThemePreference } from "@/lib/theme";

/**
 * Garde le thème cohérent PARTOUT dans l'app, pas seulement sur la page
 * Réglages.
 *
 * Le script anti-FOUC du layout applique bien `.dark` avant le 1er paint,
 * mais en mode « system » il lit `prefers-color-scheme` une seule fois, au
 * tout début du boot — un moment où le navigateur peut encore répondre
 * `false` (démarrage à froid de la PWA, hard refresh). Personne ne
 * repassait derrière : le listener de changement d'OS vivait dans
 * <ThemeToggle>, monté uniquement sur /budget/reglages. Résultat : depuis
 * n'importe quel autre module, l'app pouvait rester en clair pour toute la
 * session alors que l'OS est en sombre.
 *
 * Ce composant, monté dans le layout racine :
 *  • ré-applique la préférence au montage (auto-correction du boot),
 *  • suit les changements de thème de l'OS tant que la pref est « system ».
 */
export function ThemeSync() {
  useEffect(() => {
    // Ré-applique systématiquement : idempotent (on ne fait qu'ajouter ou
    // retirer une classe) et ça rattrape un boot script qui aurait lu
    // `prefers-color-scheme` trop tôt.
    const sync = () => applyTheme(getThemePreference());
    sync();
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", sync);
    // Retour d'arrière-plan (PWA relancée, onglet réactivé) : l'autre moment
    // où le thème a pu diverger sans qu'aucun event de média ne soit émis.
    document.addEventListener("visibilitychange", sync);
    return () => {
      mq.removeEventListener("change", sync);
      document.removeEventListener("visibilitychange", sync);
    };
  }, []);
  return null;
}
